import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Store, NOTIFICATION_TYPE } from "react-notifications-component";
import PageLoader from "components/layout/page-loader";
import SignIn from "components/auth/sign-in";
import { getClientUrls, getMyCounts, getUser } from "api/auth";
import { getUsers } from "api/user";
import { getPositions } from "api/position";
import { AuthDefault, AuthPayload, User } from "types/user";
import {
  Budget,
  CodeItType,
  CompanyType,
  ContractorType,
  Department,
  LevelType,
  LocationType,
  Position,
  Template,
} from "types/dict";
import { getLocations } from "api/location";
import { getBudgetType } from "api/budget";
import { getTemplates } from "api/template";
import {
  PaymentFilters,
  PaymentListDefault,
  PaymentListResponse,
  PaymentTableDefault,
  PaymentTableResponse,
} from "types/payment";
import { getPayments, getPaymentsTable } from "api/payment";
import {
  DocumentFilters,
  DocumentListDefault,
  DocumentListResponse,
  DocumentTableDefault,
  DocumentTableResponse,
} from "types/document";
import { getDocuments, getDocumentsTable } from "api/document";
import { getMyCalendarTasks, getMyTasks, getOtherCalendarTasks, getOtherTasks } from "api/task";
import { TaskFilters, TaskListDefault, TaskListResponse, TaskProps } from "types/task";
import { DateRange, DateRangeDefault } from "types/ui/calendar-item";
import moment from "moment";
import { emptyFilterValues, FilterValues } from "types/ui/filter";
import { SidebarCouters } from "types/ui/sidebar";
import { PageTabs } from "types/ui/tab";
import { getContractors } from "api/contractor";
import { getLevels } from "api/level";
import { getCodes } from "api/code";
import { getCompanies } from "api/companies";
import { getDepartments } from "api/departments";
import { BudgetReport, ExcelPayload, Report } from "types/report";
import {
  getBudgetRequests,
  getFormulas,
  getReportColumns,
  getReportForecasts,
  getReportTemplates,
} from "api/budget_request";
import { BudgetRequestResponse, DefaultBudgetRequestResponse, ReportTemplate } from "types/budget";
import { Formula } from "types/formula";

type StringOption = {
  value: string;
  label: string;
};

interface SharedState {
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  hash: string | null;
  setHash: React.Dispatch<React.SetStateAction<string | null>>;
  user: AuthPayload;
  getUserData: () => Promise<void>;
  users: User[];
  getUsersData: (params?: URLSearchParams) => Promise<void>;
  departments: Department[];
  getDepartmentsData: () => Promise<void>;
  positions: Position[];
  getPositionsData: () => Promise<void>;
  companies: CompanyType[];
  getCompaniesData: () => Promise<void>;
  locations: LocationType[];
  getLocationsData: () => Promise<void>;
  budgetTypes: Budget[];
  getBudgetTypesData: () => Promise<void>;
  templates: Template[];
  getTemplatesData: (params?: URLSearchParams) => Promise<void>;
  contractors: ContractorType[];
  getContractorsData: () => Promise<void>;
  levels: LevelType[];
  getLevelsData: () => Promise<void>;
  codes: CodeItType[];
  getCodesData: () => Promise<void>;
  formulas: Formula[];
  getFormulasData: () => Promise<void>;
  reportTemplates: ReportTemplate[];
  getReportTemplatesData: () => Promise<void>;
  payments: PaymentListResponse;
  getPaymentsData: (status?: number, params?: URLSearchParams) => Promise<void>;
  paymentsTable: PaymentTableResponse;
  getPaymentsTableData: (offset?: number, params?: URLSearchParams) => Promise<void>;
  documents: DocumentListResponse;
  getDocumentsData: (status?: number, params?: URLSearchParams) => Promise<void>;
  documentsTable: DocumentTableResponse;
  getDocumentsTableData: (offset?: number, params?: URLSearchParams) => Promise<void>;
  tasks: TaskListResponse;
  getTasksData: (status?: number, params?: URLSearchParams) => Promise<void>;
  calendarTasks: TaskProps[];
  getCalendarTasksData: (params?: URLSearchParams) => Promise<void>;
  currentPageTab: PageTabs;
  setCurrentPageTab: React.Dispatch<React.SetStateAction<PageTabs>>;
  resetPageTab: () => void;
  currentRange: DateRange;
  setCurrentRange: React.Dispatch<React.SetStateAction<DateRange>>;
  getCurrentRangeData: () => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  notify: (message: string, type: NOTIFICATION_TYPE, title?: string, duration?: number) => void;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  filterQuery: TaskFilters | undefined;
  setFilterQuery: React.Dispatch<React.SetStateAction<TaskFilters | undefined>>;
  filtersList: FilterValues;
  setFiltersList: React.Dispatch<React.SetStateAction<FilterValues>>;
  handleSearch: (query: string, status?: number, offset?: number) => Promise<void>;
  handleFilter: (filters: TaskFilters, status?: number) => Promise<void>;
  handleClearFilters: () => Promise<void>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  firstEntry: number;
  updateFirstEntry: (value: number) => void;
  sortersList: string[];
  setSortersList: React.Dispatch<React.SetStateAction<string[]>>;
  handleSort: (sorters: string[]) => Promise<void>;
  sortersCount: number;
  sidebarCounters: SidebarCouters | undefined;
  getSidebarCounters: () => Promise<void>;
  budgetRequests: BudgetRequestResponse;
  getBudgetRequestsData: (offset?: number) => Promise<void>;
  report: BudgetReport;
  setReport: React.Dispatch<React.SetStateAction<BudgetReport>>;
  reportDetails: any;
  setReportDetails: React.Dispatch<React.SetStateAction<any>>;
  reportType: "hierarchical" | "tabular";
  setReportType: React.Dispatch<React.SetStateAction<"hierarchical" | "tabular">>;
  excelName: string;
  setExcelName: React.Dispatch<React.SetStateAction<string>>;
  budgetColumns: StringOption[];
  getBudgetColumnsData: () => Promise<void>;
  budgetForecasts: StringOption[];
  getBudgetForecastsData: () => Promise<void>;
}

const SharedStateContext = createContext<SharedState | undefined>(undefined);

export const SharedStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const localHash = localStorage.getItem("hash");

  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [pageLoader, setPageLoader] = useState<boolean>(true);
  const [hash, setHash] = useState<string | null>(localStorage.getItem("hash"));
  const [user, setUser] = useState<AuthPayload>(AuthDefault);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [budgetTypes, setBudgetTypes] = useState<Budget[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [contractors, setContractors] = useState<ContractorType[]>([]);
  const [levels, setLevels] = useState<LevelType[]>([]);
  const [codes, setCodes] = useState<CodeItType[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [payments, setPayments] = useState<PaymentListResponse>(PaymentListDefault);
  const [paymentsTable, setPaymentsTable] = useState<PaymentTableResponse>(PaymentTableDefault);
  const [documents, setDocuments] = useState<DocumentListResponse>(DocumentListDefault);
  const [documentsTable, setDocumentsTable] = useState<DocumentTableResponse>(DocumentTableDefault);
  const [tasks, setTasks] = useState<TaskListResponse>(TaskListDefault);
  const [calendarTasks, setCalendarTasks] = useState<TaskProps[]>([]);
  const [currentPageTab, setCurrentPageTab] = useState<PageTabs>("cards");
  const [currentRange, setCurrentRange] = useState<DateRange>(DateRangeDefault);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterQuery, setFilterQuery] = useState<TaskFilters | undefined>(undefined);
  const [filtersList, setFiltersList] = useState<FilterValues>(emptyFilterValues);
  const [offset, setOffset] = useState<number>(0);
  const [firstEntry, setFirstEntry] = useState<number>(0);
  const [sortersList, setSortersList] = useState<string[]>([]);
  const [budgetRequests, setBudgetRequests] = useState<BudgetRequestResponse>(DefaultBudgetRequestResponse);
  const [report, setReport] = useState<BudgetReport>({
    report: [],
    columns: [],
    file_name: "",
    file_path: "",
    calculated_formulas: [],
  });
  const [reportDetails, setReportDetails] = useState<any>(undefined);
  const [reportType, setReportType] = useState<"hierarchical" | "tabular">("hierarchical");
  const [excelName, setExcelName] = useState<string>("");
  const [budgetColumns, setBudgetColumns] = useState<StringOption[]>([]);
  const [budgetForecasts, setBudgetForecasts] = useState<StringOption[]>([]);

  const [sidebarCounters, setSidebarCounters] = useState<SidebarCouters | undefined>(undefined);

  const updateFirstEntry = (value: number) => {
    setFirstEntry(value);
  };

  const sortersCount = useMemo(() => sortersList.length, [sortersList]);

  const [searchParams] = useSearchParams();
  const taskType = searchParams.get("type");

  const isTaskPage = useMemo(() => {
    return window.location.pathname === "/tasks";
  }, [window.location.pathname]);

  const isDocumentPage = useMemo(() => {
    return window.location.pathname === "/documents";
  }, [window.location.pathname]);

  const isPaymentPage = useMemo(() => {
    return window.location.pathname === "/payments";
  }, [window.location.pathname]);

  const isBudgetPage = useMemo(() => {
    return window.location.pathname === "/budgets";
  }, [window.location.pathname]);

  const isUserPage = useMemo(() => {
    return window.location.pathname === "/users";
  }, [window.location.pathname]);

  const isTemplatePage = useMemo(() => {
    return window.location.pathname === "/templates";
  }, [window.location.pathname]);

  const logout = () => {
    notify("Деавторизация прошла успешно", "success");
    navigate("/");
    localStorage.clear();
    setHash(null);
    setUser(AuthDefault);
    setUsers([]);
    setDepartments([]);
    setPositions([]);
    setCompanies([]);
    setLocations([]);
    setBudgetTypes([]);
    setTemplates([]);
    setContractors([]);
    setLevels([]);
    setCodes([]);
    setFormulas([]);
    setReportTemplates([]);
    setPayments(PaymentListDefault);
    setPaymentsTable(PaymentTableDefault);
    setDocuments(DocumentListDefault);
    setDocumentsTable(DocumentTableDefault);
    setTasks(TaskListDefault);
    setCalendarTasks([]);
    setCurrentPageTab("cards");
    setCurrentRange(DateRangeDefault);
    setIsAdmin(false);
    setSearchQuery("");
    setFilterQuery(undefined);
    setFiltersList(emptyFilterValues);
    setOffset(0);
    setFirstEntry(0);
    setSortersList([]);
    setBudgetRequests(DefaultBudgetRequestResponse);
    setReport({
      report: [],
      columns: [],
      file_name: "",
      file_path: "",
      calculated_formulas: [],
    });
    setReportDetails(undefined);
    setReportType("hierarchical");
    setExcelName("");
    setBudgetColumns([]);
    setBudgetForecasts([]);
    setSidebarCounters(undefined);
  };

  const notify = (message: string, type: NOTIFICATION_TYPE, title?: string, duration?: number) => {
    Store.addNotification({
      title,
      message,
      type,
      insert: "top",
      container: "top-right",
      animationIn: ["animate__animated", "animate__bounceIn"],
      animationOut: ["animate__animated", "animate__bounceOut"],
      dismiss: { duration: duration || 3000 },
    });
  };

  const getUserData = async () => {
    try {
      const userData: AuthPayload = await getUser();
      setUser(userData);
      setIsAdmin(userData.result.role === "admin");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении данных пользователя");
    }
  };

  const getUsersData = async (params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};

      const res: User[] = await getUsers(searchParamsObject as any);
      setUsers(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении пользователей");
    }
  };

  const getDepartmentsData = async () => {
    try {
      const res: Department[] = await getDepartments();
      setDepartments(
        res.filter((dep) => (isAdmin ? dep : dep.status && dep.department_id === user.result.department_id))
      );
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении отделов");
    }
  };

  const getSidebarCounters = async () => {
    const res: SidebarCouters = await getMyCounts();
    setSidebarCounters(res);
  };

  const getBudgetColumnsData = async () => {
    try {
      const res: { columns: StringOption[] } = await getReportColumns();
      setBudgetColumns(res.columns);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении колонок");
    }
  };

  const getBudgetForecastsData = async () => {
    try {
      const res: { formulas: StringOption[] } = await getReportForecasts();
      setBudgetForecasts(res.formulas);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении формул прогноза");
    }
  };

  const getPositionsData = async () => {
    try {
      const res: Position[] = await getPositions();
      setPositions(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении должностей");
    }
  };

  const getCompaniesData = async () => {
    try {
      const res: CompanyType[] = await getCompanies();
      setCompanies(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении юр. лиц");
    }
  };

  const getLocationsData = async () => {
    try {
      const res: LocationType[] = await getLocations();
      setLocations(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении филиалов");
    }
  };

  const getBudgetTypesData = async () => {
    try {
      const res: Budget[] = await getBudgetType();
      setBudgetTypes(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении статей бюджета");
    }
  };

  const getTemplatesData = async (params?: URLSearchParams) => {
    const nameSearch = params ? params.get("name") || undefined : undefined;

    try {
      const res: Template[] = await getTemplates(nameSearch);
      setTemplates(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const getContractorsData = async () => {
    try {
      const res: ContractorType[] = await getContractors();
      setContractors(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении контрагентов");
    }
  };

  const getLevelsData = async () => {
    try {
      const res: LevelType[] = await getLevels();
      setLevels(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении контрагентов");
    }
  };

  const getCodesData = async () => {
    try {
      const res: CodeItType[] = await getCodes();
      setCodes(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении кодов");
    }
  };

  const getFormulasData = async () => {
    try {
      const res: Formula[] = await getFormulas();
      setFormulas(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении формул");
    }
  };

  const getBudgetRequestsData = async (offset?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};
      if (offset) {
        searchParamsObject.offset = offset.toString();
      }

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      const res: BudgetRequestResponse = await getBudgetRequests(searchParamsObject as any);
      setBudgetRequests(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении бюджетов");
    }
  };

  const getReportTemplatesData = async () => {
    try {
      const res: ReportTemplate[] = await getReportTemplates();
      setReportTemplates(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const getPaymentsData = async (status?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      if (status !== undefined && payments[status].total_count > payments[status].data.length) {
        const payload: PaymentFilters = {
          ...(searchParamsObject as any),
          status: [status],
          offset: payments[status].data.length,
        };
        const res: PaymentListResponse = await getPayments(payload);
        setPayments({
          ...payments,
          [status]: { ...payments[status], data: payments[status].data.concat(res[status].data) },
        });
      } else {
        const res: PaymentListResponse = await getPayments(searchParamsObject as any);
        setPayments(res);
      }
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении заявок");
    }
  };

  const getPaymentsTableData = async (offset?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};
      if (offset) {
        searchParamsObject.offset = offset.toString();
      }

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      const res: PaymentTableResponse = await getPaymentsTable(searchParamsObject as any);
      setPaymentsTable(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении заявок");
    }
  };

  const getDocumentsData = async (status?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      if (status !== undefined && documents[status].total_count > documents[status].data.length) {
        const payload: DocumentFilters = {
          ...(searchParamsObject as any),
          status: [status],
          offset: documents[status].data.length,
        };
        const res: DocumentListResponse = await getDocuments(payload);
        setDocuments({
          ...documents,
          [status]: { ...documents[status], data: documents[status].data.concat(res[status].data) },
        });
      } else {
        const res: DocumentListResponse = await getDocuments(searchParamsObject as any);
        setDocuments(res);
      }
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении договоров");
    }
  };

  const getDocumentsTableData = async (offset?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};
      if (offset) {
        searchParamsObject.offset = offset.toString();
      }

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      const res: DocumentTableResponse = await getDocumentsTable(searchParamsObject as any);
      setDocumentsTable(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении договоров");
    }
  };

  const getTasksData = async (status?: number, params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      if (status !== undefined && tasks[status].total_count > tasks[status].data.length) {
        const payload: TaskFilters = {
          ...(searchParamsObject as any),
          status: [status],
          offset: tasks[status].data.length,
        };
        const res: TaskListResponse = taskType === "other" ? await getOtherTasks(payload) : await getMyTasks(payload);
        setTasks({
          ...tasks,
          [status]: { ...tasks[status], data: tasks[status].data.concat(res[status].data) },
        });
      } else {
        const res: TaskListResponse =
          taskType === "other"
            ? await getOtherTasks(searchParamsObject as any)
            : await getMyTasks(searchParamsObject as any);
        setTasks(res);
      }
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении задач");
    }
  };

  const getCalendarTasksData = async (params?: URLSearchParams) => {
    try {
      const searchParamsObject = params ? Object.fromEntries(params.entries()) : {};

      if (currentRange.start && currentRange.end) {
        searchParamsObject.from_date = moment(currentRange.start).format("YYYY-MM-DD");
        searchParamsObject.to_date = moment(currentRange.end).format("YYYY-MM-DD");
      }

      if (searchParamsObject.sort_by) {
        searchParamsObject.sort_by = Array.from(searchParamsObject.sort_by.split(",")) as any;
      }

      if (searchParamsObject.locations?.length) {
        searchParamsObject.locations = [searchParamsObject.locations] as any;
      }

      const res: TaskProps[] =
        taskType === "other"
          ? await getOtherCalendarTasks(searchParamsObject as any)
          : await getMyCalendarTasks(searchParamsObject as any);
      setCalendarTasks(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении задач");
    }
  };

  const resetPageTab = () => {
    setCurrentPageTab("cards");
  };

  const getCurrentRangeData = async () => {
    try {
      // Get initial range on mount
      const now = new Date();

      // Get first day of month
      const firstDayOfMonth = moment(now).startOf("month");
      // Get first day of week containing first day of month
      const start = moment(firstDayOfMonth).startOf("week").toDate();

      // Get last day of month
      const lastDayOfMonth = moment(now).endOf("month");
      // Get last day of week containing last day of month
      const end = moment(lastDayOfMonth).endOf("week").toDate();

      setCurrentRange({ start, end });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = async (query: string, status?: number, offset?: number) => {
    setSearchQuery(query);
    const params = new URLSearchParams();
    if (filterQuery) {
      Object.entries(filterQuery).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
    }

    if (offset) {
      setOffset(offset);
      updateFirstEntry(offset + 1);
    } else {
      setOffset(0);
      updateFirstEntry(1);
    }

    if (isTaskPage) {
      query && params.append("task_name", query);
      await getTasksData(status, params);
      await getCalendarTasksData(params);
    } else if (isDocumentPage) {
      query && params.append("card_description", query);
      await getDocumentsData(status, params);
      await getDocumentsTableData(offset, params);
    } else if (isPaymentPage) {
      query && params.append("title", query);
      await getPaymentsData(status, params);
      await getPaymentsTableData(offset, params);
    } else if (isUserPage) {
      query && params.append("name", query);
      await getUsersData(params);
    } else if (isTemplatePage) {
      query && params.append("name", query);
      await getTemplatesData(params);
    }
  };

  const handleFilter = async (filters: TaskFilters, status?: number) => {
    setFilterQuery(filters);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });

    if (offset) {
      setOffset(offset);
      updateFirstEntry(offset + 1);
    } else {
      setOffset(0);
      updateFirstEntry(1);
    }

    if (isTaskPage) {
      searchQuery && params.append("task_name", searchQuery);
      await getTasksData(status, params);
      await getCalendarTasksData(params);
    } else if (isDocumentPage) {
      searchQuery && params.append("card_description", searchQuery);
      await getDocumentsData(status, params);
      await getDocumentsTableData(offset, params);
    } else if (isPaymentPage) {
      searchQuery && params.append("title", searchQuery);
      await getPaymentsData(status, params);
      await getPaymentsTableData(offset, params);
    } else if (isBudgetPage) {
      await getBudgetRequestsData(offset, params);
    } else if (isUserPage) {
      await getUsersData(params);
    } else {
      // search only name in template
    }
  };

  const handleSort = async (sorters: string[]) => {
    setSortersList(sorters);
    const params = new URLSearchParams();

    if (filterQuery) {
      Object.entries(filterQuery).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
    }

    searchQuery && params.append("search", searchQuery);
    sorters.length > 0 && params.append("sort_by", sorters.join(","));

    if (isTaskPage) {
      await getTasksData(undefined, params);
      await getCalendarTasksData(params);
    } else if (isDocumentPage) {
      await getDocumentsData(undefined, params);
      await getDocumentsTableData(offset, params);
    } else if (isPaymentPage) {
      await getPaymentsData(undefined, params);
      await getPaymentsTableData(offset, params);
    } else if (isBudgetPage) {
      await getBudgetRequestsData(offset, params);
    }
  };

  const handleClearFilters = async () => {
    setSearchQuery("");
    setFiltersList(emptyFilterValues);
    setFilterQuery(undefined);
    setSortersList([]);
    setOffset(0);
    updateFirstEntry(1);

    if (isTaskPage) {
      await getTasksData();
      await getCalendarTasksData();
    } else if (isDocumentPage) {
      await getDocumentsData();
      await getDocumentsTableData();
    } else if (isPaymentPage) {
      await getPaymentsData();
      await getPaymentsTableData();
    } else if (isBudgetPage) {
      await getBudgetRequestsData();
    } else if (isUserPage) {
      await getUsersData();
    } else if (isTemplatePage) {
      await getTemplatesData();
    }
  };

  const initApp = async () => {
    try {
      await getClientUrls();
      hash !== null && (await getUserData());
    } catch (error) {
      console.error(error);
    } finally {
      setPageLoader(false);
    }
  };

  const value: SharedState = {
    darkMode,
    setDarkMode,
    hash,
    setHash,
    user,
    getUserData,
    users,
    getUsersData,
    departments,
    getDepartmentsData,
    positions,
    getPositionsData,
    companies,
    getCompaniesData,
    locations,
    getLocationsData,
    budgetTypes,
    getBudgetTypesData,
    templates,
    getTemplatesData,
    contractors,
    getContractorsData,
    levels,
    getLevelsData,
    codes,
    getCodesData,
    formulas,
    getFormulasData,
    reportTemplates,
    getReportTemplatesData,
    payments,
    getPaymentsData,
    paymentsTable,
    getPaymentsTableData,
    documents,
    getDocumentsData,
    documentsTable,
    getDocumentsTableData,
    tasks,
    getTasksData,
    calendarTasks,
    getCalendarTasksData,
    currentPageTab,
    setCurrentPageTab,
    resetPageTab,
    currentRange,
    setCurrentRange,
    getCurrentRangeData,
    logout,
    isAdmin,
    notify,
    searchQuery,
    setSearchQuery,
    filterQuery,
    setFilterQuery,
    filtersList,
    setFiltersList,
    handleSearch,
    handleFilter,
    handleClearFilters,
    offset,
    setOffset,
    firstEntry,
    updateFirstEntry,
    sortersList,
    setSortersList,
    handleSort,
    sortersCount,
    sidebarCounters,
    getSidebarCounters,
    budgetRequests,
    getBudgetRequestsData,
    report,
    setReport,
    reportDetails,
    setReportDetails,
    excelName,
    setExcelName,
    reportType,
    setReportType,
    budgetColumns,
    getBudgetColumnsData,
    budgetForecasts,
    getBudgetForecastsData,
  };

  const hasToken: boolean = useMemo(() => {
    if (localHash !== null) {
      setHash(localHash);
      return true;
    } else return false;
  }, [localHash]);

  const DynamicChildren = () => {
    switch (hasToken) {
      case true:
        if (!pageLoader) {
          return children;
        } else {
          return <PageLoader />;
        }

      default:
        return <SignIn />;
    }
  };

  useEffect(() => {
    initApp();
  }, [hash]);

  useEffect(() => {
    user.result.date_joined && getSidebarCounters();
  }, [user.result.date_joined]);

  useEffect(() => {
    const tablerTheme = localStorage.getItem("tablerTheme");

    if (tablerTheme === "dark") {
      setDarkMode(true);
      document.body.setAttribute("data-bs-theme", tablerTheme);
    } else {
      setDarkMode(false);
      document.body.removeAttribute("data-bs-theme");
    }
  }, [darkMode]);

  return <SharedStateContext.Provider value={value}>{DynamicChildren()}</SharedStateContext.Provider>;
};

export const useSharedState = (): SharedState => {
  const context = useContext(SharedStateContext);
  if (!context) {
    throw new Error("useSharedState must be used within a SharedStateProvider");
  }
  return context;
};
